"use client";

import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Link from "next/link";
import Image from "next/image";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Container } from "@/components/layout/container";
import { getBlogs, type BlogSummary } from "@/api/blogs";
import { useScrollTriggerRefresh } from "@/lib/use-scroll-trigger-refresh";

if (typeof window !== "undefined") gsap.registerPlugin(ScrollTrigger);

// Related posts must be picked deterministically: this component renders on the server (into the
// initial HTML) and again on the client during hydration. A random pick (Math.random) produces a
// different set each run, so the two renders disagree and hydration fails — which makes React throw
// away the server HTML and regenerate the whole page (the blank-flash-on-refresh symptom). Ordering
// by slug keeps both renders identical while still varying the set per post.
function pickStable<T extends { slug: string }>(items: T[], count: number, seed: string): T[] {
  return [...items]
    .sort((a, b) => `${seed}:${a.slug}`.localeCompare(`${seed}:${b.slug}`))
    .slice(0, count);
}

function getRelatedBlogs(
  blogs: BlogSummary[],
  currentSlug: string,
  currentCategory?: string
): BlogSummary[] {
  const filtered = blogs.filter((b) => b.slug !== currentSlug);

  if (currentCategory) {
    const sameCategoryBlogs = filtered.filter((b) => b.category === currentCategory);
    if (sameCategoryBlogs.length >= 3) {
      return pickStable(sameCategoryBlogs, 3, currentSlug);
    }
    const otherBlogs = filtered.filter((b) => b.category !== currentCategory);
    return [
      ...sameCategoryBlogs,
      ...pickStable(otherBlogs, 3 - sameCategoryBlogs.length, currentSlug),
    ].slice(0, 3);
  }

  return pickStable(filtered, 3, currentSlug);
}

export function MoreBlogs({
  currentSlug,
  currentCategory,
  initialBlogs,
}: {
  currentSlug: string;
  currentCategory?: string;
  initialBlogs?: BlogSummary[];
}) {
  const rootRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["blogs"],
    queryFn: getBlogs,
    initialData: initialBlogs,
  });
  const pool = isLoading ? null : (data ?? []);

  const [others, setOthers] = useState<BlogSummary[] | null>(
    initialBlogs ? getRelatedBlogs(initialBlogs, currentSlug, currentCategory) : null
  );

  useEffect(() => {
    if (!pool) return;
    setOthers(getRelatedBlogs(pool, currentSlug, currentCategory));
  }, [pool, currentSlug, currentCategory]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || !others || others.length === 0) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        "[data-card]",
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          stagger: 0.08,
          ease: "power3.out",
          scrollTrigger: { trigger: root, start: "top 85%", end: "top 55%", scrub: true },
        }
      );
    }, root);

    return () => ctx.revert();
  }, [others]);

  useScrollTriggerRefresh([others]);

  const stillLoading = pool === null;
  if (!stillLoading && others !== null && others.length === 0) return null;

  return (
    <section ref={rootRef} className="relative py-10 sm:py-[7vh]">
      <Container>
        <span className="font-mono-tech text-xs uppercase tracking-[0.14em] text-[var(--color-text-tertiary)]">
          More articles
        </span>
        <div className="mt-6 grid gap-6 sm:grid-cols-3">
          {stillLoading || others === null
            ? Array.from({ length: 3 }, (_, i) => (
                <div key={i} className="overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
                  <div className="aspect-[16/10] w-full animate-pulse bg-[var(--color-bg)]" />
                  <div className="p-5">
                    <div className="h-5 w-3/4 animate-pulse rounded bg-[var(--color-bg)]" />
                    <div className="mt-2 h-3.5 w-1/2 animate-pulse rounded bg-[var(--color-bg)]" />
                  </div>
                </div>
              ))
            : others.map((b) => (
            <Link
              key={b.slug}
              href={`/blog/${b.slug}`}
              data-card
              className="group flex flex-col overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-elevated)] transition-colors hover:border-[var(--color-primary)]"
            >
              <div className="relative aspect-[16/10] w-full flex-shrink-0">
                <Image
                  src={b.coverImage}
                  alt={b.title}
                  fill
                  sizes="(min-width: 640px) 33vw, 100vw"
                  className="object-cover"
                />
              </div>
              <div className="flex flex-col flex-grow p-5">
                <h3 className="font-display text-base font-bold leading-tight text-[var(--color-text-primary)]">{b.title}</h3>
                <p className="mt-auto pt-3 text-sm text-[var(--color-text-tertiary)]">{b.category}</p>
              </div>
            </Link>
          ))}
        </div>
      </Container>
    </section>
  );
}
